import { storedValue } from "idfweb-settings";
import type { OpenEditors } from "idfweb-workbench";

import type { RememberedBoard } from "./board.ts";

/**
 * What the page remembers about each kept project between visits: the
 * configure preset last chosen for each project root, the root last
 * entered in a tree of several projects, the chip target
 * chosen in the dropdown, and how its board was connected. One stored value
 * per project, `idfweb.project.<id>`, holds it as JSON, so deleting the
 * project removes it in one write. While the open project is not kept,
 * nothing is remembered.
 *
 * The open editors and the page's layout are remembered too. The layout is
 * also kept apart from any project, as the last used one, which a page that
 * opens no kept project starts from.
 */

interface ProjectState {
    /** Preset names by the root's guest path. */
    presets?: Record<string, string>;
    /** The project root last entered, as a guest path, for a tree that holds several projects. */
    root?: string;
    target?: string;
    board?: RememberedBoard;
    editors?: OpenEditors;
    layout?: Layout;
}

/** The side bars and the output panel as the page last showed them. */
export interface Layout {
    sidebar: boolean;
    /** The left side bar's width in pixels, once its edge has been dragged. */
    sidebarWidth?: number;
    /** The right-hand column, which holds the output panel. */
    side: boolean;
    /** The output panel's tab. */
    tab?: string;
    /** The width of the right-hand column, or its height when the panes are stacked, in pixels. */
    split?: { stacked: boolean; size: number };
}

const LAST_LAYOUT = "idfweb.layout";

const KEY_PREFIX = "idfweb.project.";

let project: string | undefined;

/** The kept project whose state the functions below read and write, or undefined for none. */
export function setStateProject(id: string | undefined): void {
    project = id;
}

export function storedPreset(root: string): string | undefined {
    return read().presets?.[root];
}

export function storePreset(root: string, name: string): void {
    update((state) => ({ ...state, presets: { ...state.presets, [root]: name } }));
}

export function storedRoot(): string | undefined {
    return read().root;
}

export function storeRoot(root: string): void {
    update((state) => ({ ...state, root }));
}

export function storedTarget(): string | undefined {
    return read().target;
}

export function storeTarget(target: string): void {
    update((state) => ({ ...state, target }));
}

export function storedBoard(): RememberedBoard | undefined {
    return read().board;
}

export function storeBoard(board: RememberedBoard | undefined): void {
    update((state) => ({ ...state, board }));
}

export function storedEditors(): OpenEditors | undefined {
    const editors = read().editors;
    return Array.isArray(editors?.tabs) && Array.isArray(editors.rendered) ? editors : undefined;
}

export function storeEditors(editors: OpenEditors): void {
    update((state) => ({ ...state, editors }));
}

/** The layout stored for the project `id`, by default the open one. */
export function storedLayout(id = project): Layout | undefined {
    return read(id).layout;
}

/** The layout last used with any project, or with none. */
export function lastLayout(): Layout | undefined {
    return parse(storedValue(LAST_LAYOUT).get()) as Layout | undefined;
}

/** Store the layout for the open project, if it is kept, and as the last used. */
export function storeLayout(layout: Layout): void {
    storedValue(LAST_LAYOUT).set(JSON.stringify(layout));
    update((state) => ({ ...state, layout }));
}

export function forgetProjectState(id: string): void {
    storedValue(KEY_PREFIX + id).set(undefined);
}

function read(id = project): ProjectState {
    if (id === undefined) return {};
    return (parse(storedValue(KEY_PREFIX + id).get()) as ProjectState | undefined) ?? {};
}

function parse(json: string | undefined): object | undefined {
    try {
        const value: unknown = JSON.parse(json ?? "null");
        return typeof value === "object" && value !== null ? value : undefined;
    } catch {
        return undefined;
    }
}

function update(change: (state: ProjectState) => ProjectState): void {
    if (project === undefined) return;
    storedValue(KEY_PREFIX + project).set(JSON.stringify(change(read())));
}
